import { Injectable, signal, WritableSignal, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AioLoanInfoVm, BaseRequest, Item, UpdateLoanStatusAioLoanVm } from './models/loan-1003-clear1.model';
import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';

export const REQUIRES_SECURITY_PROFILE = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root',
})
export class AioService {
  private readonly _environment = inject(APP_ENV);
  private readonly _http = inject(HttpClient);

  private readonly _saveAioSub: Subject<any> = new Subject();
  saveAio$: Observable<any> = this._saveAioSub.asObservable();
  private _aioFormHasUnsavedChanges: WritableSignal<boolean> = signal(false);
  readonly aioFormHasUnsavedChanges = this._aioFormHasUnsavedChanges.asReadonly();
  //InitializeModel method
  InitializeModel(): AioLoanInfoVm {
    return {
      loanAmount: 0,
      intRate: 0,
      firstRateChange: 0,
      nextRateChange: 0,
      lifeCap: 0,
      floor: 0,
      qualifyingRate: 0,
      margin: 0,
      firstAdjCap: 0,
      nextAdjCap: 0,
      noAdjCap: false,
      armMaxRate: 0,
      repaymentPerid: 0,
      indexType: 0,
      indexName: '',
      armIndexValueForWeekEnding: undefined,
      armIndexPublished: '',
      lookbackDays: 0,
      escrowWaiver: '',
      cashInHand: 0,
      initialDrawAmount: 0,
      maxDrawAmount: 0,
      unusedFunds: 0,
      initialAdvanceAMount: 0,
      borrower1ContactNumber: '',
      borrower2ContactNumber: '',
      firstPaymentDate: undefined,
      qaCallRequestDate: undefined,
      qaCallCompleteDate: undefined,
      welcomeLetterRequestDate: undefined,
      note: '',
      userId: 0,
      loanId: 0,
      channel: 0,
      fileDataId: 0,
      fileName: '',
      organizationId: 0,
    };
  }

  GetAioLoanInfo = (baseRequest?: BaseRequest): Observable<any> => {
    const url = `${this._environment.retail.apiUrl}/AioLoanInfo1dot5/GetAioLoanInfo`;
    return this._http.post(url, baseRequest, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });
  };

  SaveAioLoanInformation(loanInfoModel: AioLoanInfoVm): Observable<any> {
    const url = `${this._environment.retail.apiUrl}/AioLoanInfo1dot5/SaveAIOLoanInfo`;
    return this._http.post(url, loanInfoModel, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });
  }

  UpdateLoanStatusAioLoan(request: UpdateLoanStatusAioLoanVm): Observable<any> {
    const url = `${this._environment.retail.apiUrl}/AioLoanInfo1dot5/UpdateLoanStatus`;
    return this._http.put(url, request, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });
  }

  GetLockStatusAioLoan(fileDataId): Observable<any> {
    const url = `${this._environment.retail.apiUrl}/AioLoanInfo1dot5/GetLockStatus?fileDataId=${fileDataId}`;
    return this._http.get(url, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });
  }

  EscrowWaiverType = (): Item[] => {
    return [
      {
        id: 0,
        value: 'Select',
      },
      {
        id: 1,
        value: 'Waive All',
      },
      {
        id: 2,
        value: 'Not Waived',
      },
      {
        id: 3,
        value: 'Waive Taxes Only',
      },
      {
        id: 4,
        value: 'Waive Insurance Only',
      },
    ];
  };

  GetARMRoundingDropdown = (): Item[] => {
    return [
      {
        id: 0,
        value: 'No Rounding',
      },
      {
        id: 1,
        value: 'To the nearest 1/8 %',
      },
      {
        id: 2,
        value: 'Up to the nearest 1/8 %',
      },
      {
        id: 3,
        value: 'Down to the nearest 1/8 %',
      },
      {
        id: 4,
        value: 'To the nearest 1/4 %',
      },
      {
        id: 5,
        value: 'Up to the nearest 1/4 %',
      },
      {
        id: 6,
        value: 'To the nearest 1/8 %',
      },
    ];
  };

  GetQualRateOptionList = (): Item[] => {
    return [
      { id: 0, value: 'Select' },
      { id: 1, value: 'Note Rate' },
      { id: 2, value: 'Max Rate in First 5 Years' },
      { id: 3, value: 'Rate+2 or Unrounded Fully Indexed Rate' },
      { id: 4, value: 'Rate or Unrounded Fully Indexed Rate' },
      { id: 5, value: 'Rate+1' },
      { id: 6, value: 'Rate+2 or Rounded Fully Indexed Rate' },
      { id: 7, value: 'Rate or Rounded Fully Indexed Rate' },
    ];
  };

  GetIntOnlyQualPaymentCalcOptionList = (): Item[] => {
    return [
      { id: 0, value: 'Default' },
      { id: 1, value: 'Amortizing Over Full Term' },
      { id: 2, value: 'Amortizing Over Non Interest Only Term' },
      { id: 3, value: 'Interest Only' },
    ];
  };

  GetAIOUniqueCircList = (): Item[] => {
    return [
      { id: 0, value: 'No' },
      { id: 1, value: 'Yes' },
    ];
  };

  GetServicePreferenceList = (): Item[] => {
    return [
      { id: 0, value: 'Merchants Bank of Indiana' },
      { id: 1, value: 'Northpoint Bank' },
      { id: 2, value: 'Keypointe Credit Union' },
      { id: 3, value: 'None' },
    ];
  };

  saveAIO(successCallbackFn: any) {
    this._saveAioSub.next(successCallbackFn);
  }

  setAioFormUnsavedChangesState(value: boolean) {
    this._aioFormHasUnsavedChanges.set(value);
  }
}
